import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useCompanyPermissions } from "@/hooks/useCompanyPermissions";
import { useActiveCompany } from "@/contexts/ActiveCompanyContext";
import { useRoleMode } from "@/contexts/RoleModeContext";
import { useOnboardingStatusWithSession } from "@/hooks/useOnboardingStatus";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

interface EmployerRouteProps {
  children: React.ReactNode;
}

export function EmployerRoute({ children }: EmployerRouteProps) {
  const navigate = useNavigate();
  const { mode, setMode } = useRoleMode();
  const { role, loading } = useOnboardingStatusWithSession();
  const { activeCompany, isLoading: companyLoading, companies } = useActiveCompany();
  const { canViewApplications } = useCompanyPermissions();

  // Combined loading state
  const isLoading = loading || companyLoading;

  // Auto-switch to employer mode if user has employer role but wrong mode
  useEffect(() => {
    if (role === "employer" && mode !== "employer") {
      setMode("employer");
    }
  }, [role, mode, setMode]);

  // Show loader only if NO cached data exists
  if (isLoading && !role && companies.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your companies...</p>
        </div>
      </div>
    );
  }

  // Check if user has employer permission in database (only after loading completes)
  if (role !== "employer" && companies.length === 0) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check UI mode
  if (mode !== "employer") {
    return <Navigate to="/dashboard" replace />;
  }

  // If companies exist but activeCompany not set yet, still initializing
  if (!activeCompany && companies.length > 0) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Initializing workspace...</p>
        </div>
      </div>
    );
  }

  // No company membership - redirect to setup (only after loading completes)
  if (!activeCompany && companies.length === 0) {
    return <Navigate to="/employer-setup" replace />;
  }

  // Has company but no permission to view applications
  if (!canViewApplications) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>You don't have permission to access the Applicant Tracking System.</p>
            <p className="text-sm">
              Contact your company administrator if you need access to manage applications.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="w-full">
              Go to Dashboard
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
